import { isClientCacheBypassActive, withClientCacheBypass } from "./analyticsClientCacheBypass";
import type {
  AnalyticsDashboardBootstrap,
  AnalyticsRefreshStatus,
  AnalyticsCacheStatus,
  AnalyticsCacheInvalidateResponse,
  AnalyticsDataQualityHealth,
  CategoryData,
  CategoryTrendPoint,
  DataQualityIssueListResult,
  DataQualityIssueType,
  DataQualitySortBy,
  DataQualitySortDir,
  DataQualityTopOffendersResult,
  DataQualityTrendResult,
  PilotDataQualityIntakeReport,
  DailySale,
  DashboardAdvancedSnapshot,
  DashboardValidationEndpoint,
  ForecastDto,
  ForecastBaselineBacktestDto,
  GenderData,
  HourData,
  InventoryAlertListDto,
  InventoryStatus,
  InventoryInsights,
  InventoryItemDetail,
  PaymentData,
  QuickInsights,
  ProductDecisionCenterResponse,
  ProductDecisionTimelineFilterResponse,
  ProductDecisionTimelineExportResponse,
  RebalanceListDto,
  ReorderSuggestion,
  InventoryStoreComparison,
  InventoryActionWorkflow,
  InventoryActionDecisionInput,
  InventoryReportSchedule,
  InventoryReportScheduleInput,
  InventoryScheduleRunResponse,
  SalesSummary,
  SizeCurveDto,
  DecisionBoardAggregateResponse,
  StoreOption,
  SupplierFilterOption,
  SupplierData,
  TopProductsAdvancedResult,
  TopProductsResult,
  TransactionStats,
  WeekdayData,
  AnalyticsActionItem,
  AnalyticsActionListResponse,
  AnalyticsActionCounts,
  AnalyticsActionUpsertInput,
  AnalyticsActionStatusUpdateInput,
  AnalyticsActionOutcomeUpdateInput,
  AnalyticsActionOutcomeSummaryFilters,
  AnalyticsActionOutcomeSummaryResponse,
  AnalyticsActionFilters,
  AnalyticsActionPopulationFilters,
  AnalyticsResponseMeta,
  SupplierDecisionDurableReport,
  PilotIntakeDurableReport,
} from "../types/analytics";
import type { DocumentOperationResponse } from "./exportApi";
import { ensureExportAdminKey } from "./exportApi";
import { apiUrl } from "../utils/apiUrl";
import { appendSupplierDecisionReportQuery } from "./supplierDecisionReportQuery";
import { appendDataScopeToParams, normalizeDataScope } from "../utils/dataScope";
import {
  API_FAILOVER_TIMEOUT_MS_OPTION,
  type ApiFailoverRequestInit,
} from "../utils/apiFailover";
import { fetchWithTimeout, FetchTimeoutError } from "../utils/fetchWithTimeout";
import { API_COLD_START_TIMEOUT_MS, getRetryTimeouts } from "../utils/apiTimeouts";
import {
  AnalyticsMetaError,
  assertAnalyticsMetaSuccess as assertAnalyticsMetaSuccessShared,
} from "../utils/analyticsResponseMeta";
import type { ZodType } from "zod";
import {
  analyticsActionCountsResponseSchema,
  analyticsActionListResponseSchema,
  analyticsActionOutcomeSummaryResponseSchema,
  dashboardAdvancedResponseSchema,
  dashboardBootstrapResponseSchema,
  dataQualityHealthResponseSchema,
  dataQualityIssueListResponseSchema,
  dataQualityTopOffendersResponseSchema,
  dataQualityTrendResponseSchema,
  decisionBoardAggregateResponseSchema,
  durableAnalyticsReportResponseSchema,
  inventoryActionWorkflowResponseSchema,
  inventoryBalanceResponseSchema,
  inventoryDetailResponseSchema,
  inventoryInsightsResponseSchema,
  inventoryPagedResponseSchema,
  inventorySignalResponseSchema,
  inventoryStoreComparisonResponseSchema,
  pilotDataQualityIntakeResponseSchema,
  productDecisionCenterResponseSchema,
} from "../validation/analyticsResponseSchemas";
import { validateAnalyticsResponse } from "../validation/analyticsResponseValidation";

const DEFAULT_CLIENT_CACHE_TTL_MS = 15_000;
const DEFAULT_ANALYTICS_GET_TIMEOUT_MS = API_COLD_START_TIMEOUT_MS;
const responseCache = new Map<string, { expiresAt: number; value: unknown }>();
const inFlightRequests = new Map<string, Promise<unknown>>();
let clientCacheGeneration = 0;
const ANALYTICS_CACHE_CHANNEL_NAME = "trendplus-analytics-cache";
let analyticsCacheChannel: BroadcastChannel | null | undefined;

function getAnalyticsCacheChannel(): BroadcastChannel | null {
  if (analyticsCacheChannel !== undefined) return analyticsCacheChannel;
  if (typeof BroadcastChannel === "undefined") {
    analyticsCacheChannel = null;
    return null;
  }
  try {
    analyticsCacheChannel = new BroadcastChannel(ANALYTICS_CACHE_CHANNEL_NAME);
    analyticsCacheChannel.addEventListener("message", (event: MessageEvent) => {
      if ((event.data as { type?: string } | null)?.type === "invalidate") {
        // Another tab saw an import/refresh/clear: drop this tab's copies without re-broadcasting.
        invalidateAnalyticsCache({ broadcast: false });
      }
    });
  } catch {
    analyticsCacheChannel = null;
  }
  return analyticsCacheChannel;
}

function abortReason(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException("The operation was aborted.", "AbortError");
}

/**
 * Lets one caller stop waiting without cancelling a request that other callers share.
 */
function withCallerSignal<T>(request: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return request;
  if (signal.aborted) return Promise.reject(abortReason(signal));
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(abortReason(signal));
    signal.addEventListener("abort", onAbort, { once: true });
    request.then(
      (value) => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      (error: unknown) => {
        signal.removeEventListener("abort", onAbort);
        reject(error);
      },
    );
  });
}

/**
 * Runs a query start so that analytics GETs issued synchronously by it skip the browser cache
 * (manual refresh). The fresh response still replaces the cached entry.
 */
export { withClientCacheBypass };

type FailoverAwareWindow = Window & {
  __trendplusFailoverInstalled?: boolean;
};

export { AnalyticsMetaError };

type AnalyticsArrayWithMeta<T> = T[] & {
  meta?: AnalyticsResponseMeta | null;
};

type FetchJsonResponseHandler<T> = (response: Response, payload: T) => T;

export function makeUrl(path: string, params?: URLSearchParams) {
  const baseUrl = apiUrl(path);
  const finalParams = params ? new URLSearchParams(params.toString()) : new URLSearchParams();
  if (path.startsWith("/api/analytics")) {
    appendDataScopeToParams(finalParams);
  }

  return finalParams && Array.from(finalParams.keys()).length > 0
    ? `${baseUrl}?${finalParams.toString()}`
    : baseUrl;
}

function appendFilterParams(
  params: URLSearchParams,
  fromDate?: string,
  toDate?: string,
  storeId?: number | null,
  supplierId?: number | null
) {
  if (fromDate) params.append("fromDate", fromDate);
  if (toDate) params.append("toDate", toDate);
  if (storeId != null) params.append("storeId", String(storeId));
  if (supplierId != null) params.append("supplierId", String(supplierId));
}

function isApiFailoverLayerActive(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean((window as FailoverAwareWindow).__trendplusFailoverInstalled);
}

async function fetchAnalyticsResponse(
  url: string,
  init: RequestInit | undefined,
  timeoutMs: number
): Promise<Response> {
  if (isApiFailoverLayerActive()) {
    const failoverInit: ApiFailoverRequestInit = {
      ...init,
      [API_FAILOVER_TIMEOUT_MS_OPTION]: timeoutMs,
    };
    return fetch(url, failoverInit);
  }

  return fetchWithTimeout(url, init, timeoutMs);
}

/**
 * Fetch with retry on timeout (for cold-start backends).
 * Tries with shorter timeout first, then retries with longer timeout if first attempt times out.
 */
async function fetchJsonWithRetry<T>(
  url: string,
  timeoutMs: number,
  errorMessage?: string,
  onResponse?: FetchJsonResponseHandler<T>,
  schema?: ZodType<unknown>,
  signal?: AbortSignal,
): Promise<T> {
  if (isApiFailoverLayerActive()) {
    const res = await fetchAnalyticsResponse(url, signal ? { signal } : undefined, timeoutMs);
    if (!res.ok) {
      throw new Error(await parseApiError(res, errorMessage));
    }
    const payload = (await res.json()) as T;
    const processedPayload = onResponse?.(res, payload) ?? payload;
    const checkedPayload = assertAnalyticsMetaSuccess(processedPayload, errorMessage);
    return schema
      ? validateAnalyticsResponse<T>(checkedPayload, schema, errorMessage ?? "Analytics")
      : checkedPayload;
  }

  const { firstAttemptTimeoutMs, totalTimeoutMs } = getRetryTimeouts(timeoutMs);
  
  try {
    const res = await fetchAnalyticsResponse(url, signal ? { signal } : undefined, firstAttemptTimeoutMs);
    if (!res.ok) {
      throw new Error(await parseApiError(res, errorMessage));
    }
    const payload = (await res.json()) as T;
    const processedPayload = onResponse?.(res, payload) ?? payload;
    const checkedPayload = assertAnalyticsMetaSuccess(processedPayload, errorMessage);
    return schema
      ? validateAnalyticsResponse<T>(checkedPayload, schema, errorMessage ?? "Analytics")
      : checkedPayload;
  } catch (error) {
    // Don't retry on non-timeout errors
    if (!(error instanceof FetchTimeoutError)) {
      throw error;
    }

    // First attempt timed out - retry with longer timeout
  const res = await fetchAnalyticsResponse(url, signal ? { signal } : undefined, totalTimeoutMs);
    if (!res.ok) {
      throw new Error(await parseApiError(res, errorMessage));
    }
    const payload = (await res.json()) as T;
    const processedPayload = onResponse?.(res, payload) ?? payload;
    const checkedPayload = assertAnalyticsMetaSuccess(processedPayload, errorMessage);
    return schema
      ? validateAnalyticsResponse<T>(checkedPayload, schema, errorMessage ?? "Analytics")
      : checkedPayload;
  }
}

async function fetchJson<T>(
  path: string,
  params?: URLSearchParams,
  errorMessage?: string,
  onResponse?: FetchJsonResponseHandler<T>,
  schema?: ZodType<unknown>,
  signal?: AbortSignal,
): Promise<T> {
  const finalParams = params ? new URLSearchParams(params.toString()) : undefined;
  const url = makeUrl(path, finalParams);
  const cacheTtlMs = resolveClientCacheTtl(path);
  const bypassClientCache = isClientCacheBypassActive();
  getAnalyticsCacheChannel();

  if (cacheTtlMs <= 0) {
    return fetchJsonWithRetry<T>(url, DEFAULT_ANALYTICS_GET_TIMEOUT_MS, errorMessage, onResponse, schema, signal);
  }

  if (!bypassClientCache) {
    const cached = responseCache.get(url);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.value as T;
    }

    const existingRequest = inFlightRequests.get(url) as Promise<T> | undefined;
    if (existingRequest) {
      return withCallerSignal(existingRequest, signal);
    }
  }

  // The shared request is not bound to any caller's signal: one caller aborting (filter change,
  // unmount) must not reject the other callers that joined the same in-flight request.
  const requestGeneration = clientCacheGeneration;
  const request = (async () => {
    const data = await fetchJsonWithRetry<T>(
      url,
      DEFAULT_ANALYTICS_GET_TIMEOUT_MS,
      errorMessage,
      onResponse,
      schema,
    );
    if (requestGeneration === clientCacheGeneration) {
      responseCache.set(url, { expiresAt: Date.now() + cacheTtlMs, value: data });
    }

    return data;
  })();

  inFlightRequests.set(url, request as Promise<unknown>);
  void request
    .finally(() => {
      if (inFlightRequests.get(url) === request) {
        inFlightRequests.delete(url);
      }
    })
    .catch(() => undefined);

  return withCallerSignal(request, signal);
}

async function fetchJsonWithCachedFallback<T>(
  cachedPath: string,
  fallbackPath: string,
  params?: URLSearchParams,
  errorMessage?: string,
  schema?: ZodType<unknown>,
  signal?: AbortSignal,
): Promise<T> {
  try {
    return await fetchJson<T>(cachedPath, params, errorMessage, undefined, schema, signal);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    const normalized = message.toLowerCase();
    if (!normalized.includes("404") && !normalized.includes("not found")) {
      throw error;
    }

    return fetchJson<T>(fallbackPath, params, errorMessage, undefined, schema, signal);
  }
}

async function postJson<T>(path: string, body: unknown, errorMessage?: string, adminKey?: string): Promise<T> {
  const timeoutMs = DEFAULT_ANALYTICS_GET_TIMEOUT_MS;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (adminKey?.trim()) {
    headers["X-Admin-Key"] = adminKey.trim();
  }
  const init: RequestInit = {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  };

  if (isApiFailoverLayerActive()) {
    const response = await fetchAnalyticsResponse(makeUrl(path), init, timeoutMs);
    if (!response.ok) {
      throw new Error(await parseApiError(response, errorMessage));
    }

    const payload = (await response.json()) as T;
    return assertAnalyticsMetaSuccess(payload, errorMessage);
  }

  const { firstAttemptTimeoutMs, totalTimeoutMs } = getRetryTimeouts(timeoutMs);
  
  try {
    const response = await fetchAnalyticsResponse(makeUrl(path), init, firstAttemptTimeoutMs);

    if (!response.ok) {
      throw new Error(await parseApiError(response, errorMessage));
    }

    const payload = (await response.json()) as T;
    return assertAnalyticsMetaSuccess(payload, errorMessage);
  } catch (error) {
    // Don't retry on non-timeout errors
    if (!(error instanceof FetchTimeoutError)) {
      throw error;
    }

    // First attempt timed out - retry with longer timeout
    const response = await fetchAnalyticsResponse(makeUrl(path), init, totalTimeoutMs);

    if (!response.ok) {
      throw new Error(await parseApiError(response, errorMessage));
    }

    const payload = (await response.json()) as T;
    return assertAnalyticsMetaSuccess(payload, errorMessage);
  }
}

async function patchJson<T>(path: string, body: unknown, errorMessage?: string): Promise<T> {
  const timeoutMs = DEFAULT_ANALYTICS_GET_TIMEOUT_MS;
  const init: RequestInit = {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  };

  if (isApiFailoverLayerActive()) {
    const response = await fetchAnalyticsResponse(makeUrl(path), init, timeoutMs);
    if (!response.ok) {
      throw new Error(await parseApiError(response, errorMessage));
    }

    const payload = (await response.json()) as T;
    return assertAnalyticsMetaSuccess(payload, errorMessage);
  }

  const { firstAttemptTimeoutMs, totalTimeoutMs } = getRetryTimeouts(timeoutMs);
  
  try {
    const response = await fetchAnalyticsResponse(makeUrl(path), init, firstAttemptTimeoutMs);

    if (!response.ok) {
      throw new Error(await parseApiError(response, errorMessage));
    }

    const payload = (await response.json()) as T;
    return assertAnalyticsMetaSuccess(payload, errorMessage);
  } catch (error) {
    // Don't retry on non-timeout errors
    if (!(error instanceof FetchTimeoutError)) {
      throw error;
    }

    // First attempt timed out - retry with longer timeout
    const response = await fetchAnalyticsResponse(makeUrl(path), init, totalTimeoutMs);

    if (!response.ok) {
      throw new Error(await parseApiError(response, errorMessage));
    }

    const payload = (await response.json()) as T;
    return assertAnalyticsMetaSuccess(payload, errorMessage);
  }
}

function resolveClientCacheTtl(path: string): number {
  if (path.includes("/api/analytics/cached/filters/stores")) return 5 * 60_000;
  if (path.includes("/api/analytics/cached/filters/suppliers")) return 60_000;
  if (path.includes("/api/analytics/cached/dashboard/bootstrap")) return 30_000;
  if (path.includes("/api/analytics/cached/inventory/forecast")) return 5 * 60_000;
  if (path.includes("/api/analytics/cached/inventory/size-curve")) return 5 * 60_000;
  if (path.includes("/api/analytics/cached/inventory/rebalance-suggestions")) return 2 * 60_000;
  if (path.includes("/api/analytics/cached/inventory/alerts")) return 60_000;
  if (path.includes("/api/analytics/cached/")) return DEFAULT_CLIENT_CACHE_TTL_MS;
  return 0;
}

function assertAnalyticsMetaSuccess<T>(payload: T, fallbackMessage?: string): T {
  return assertAnalyticsMetaSuccessShared(
    payload,
    (candidate) => {
      if (!candidate || typeof candidate !== "object") {
        return null;
      }

      const meta = (candidate as { meta?: unknown }).meta;
      return meta && typeof meta === "object"
        ? (meta as AnalyticsResponseMeta)
        : null;
    },
    fallbackMessage ?? "Podaci trenutno nisu dostupni."
  );
}

type ApiErrorPayload = {
  detail?: string;
  title?: string;
  message?: string;
  correlationId?: string;
};

function appendCorrelationId(message: string, correlationId?: string | null): string {
  const trimmedCorrelationId = correlationId?.trim();
  if (!trimmedCorrelationId) {
    return message;
  }

  return message.includes(trimmedCorrelationId)
    ? message
    : `${message} (Correlation ID: ${trimmedCorrelationId})`;
}

async function parseApiError(res: Response, fallbackMessage?: string): Promise<string> {
  const contentType = res.headers.get("content-type") ?? "";
  const headerCorrelationId = res.headers.get("X-Correlation-ID") ?? res.headers.get("x-correlation-id") ?? null;

  if (contentType.includes("application/json")) {
    const payload = (await res.json().catch(() => null)) as ApiErrorPayload | null;
    const detail = payload?.detail ?? payload?.message ?? payload?.title;
    const correlationId = payload?.correlationId ?? headerCorrelationId;
    if (detail && fallbackMessage) {
      const normalizedDetail = detail.startsWith(fallbackMessage) ? detail : `${fallbackMessage}: ${detail}`;
      return appendCorrelationId(normalizedDetail, correlationId);
    }
    if (detail) return appendCorrelationId(detail, correlationId);
    if (correlationId) {
      return appendCorrelationId(fallbackMessage ?? `HTTP ${res.status}`, correlationId);
    }
  }

  const text = (await res.text()).trim();
  if (text && fallbackMessage) {
    return appendCorrelationId(
      text.startsWith(fallbackMessage) ? text : `${fallbackMessage}: ${text}`,
      headerCorrelationId
    );
  }

  if (text) return appendCorrelationId(text, headerCorrelationId);
  return appendCorrelationId(fallbackMessage ?? `HTTP ${res.status}`, headerCorrelationId);
}

export async function checkAnalyticsHealth(): Promise<{
  status: string;
  tables: { salesFacts: number; salesLineFacts: number; productsDim: number };
  message: string;
}> {
  return fetchJson("/api/analytics/health", undefined, "Provera zdravlja analytics baze nije uspela.");
}

export async function getSalesSummary(
  fromDate?: string,
  toDate?: string,
  useCached = true,
  storeId?: number | null,
  supplierId?: number | null
): Promise<SalesSummary> {
  const params = new URLSearchParams();
  appendFilterParams(params, fromDate, toDate, storeId, supplierId);

  return fetchJson(
    useCached ? "/api/analytics/cached/sales/summary" : "/api/analytics/sales/summary",
    params,
    "Greška pri učitavanju sažetka prodaje"
  );
}

export async function getTopProducts(
  top = 20,
  fromDate?: string,
  toDate?: string,
  useCached = true,
  storeId?: number | null,
  supplierId?: number | null,
  dataScope?: string | null
): Promise<TopProductsResult> {
  const params = new URLSearchParams({ top: String(top) });
  appendFilterParams(params, fromDate, toDate, storeId, supplierId);
  if (dataScope) params.set("dataScope", normalizeDataScope(dataScope));

  return fetchJson(
    useCached ? "/api/analytics/cached/sales/top-products" : "/api/analytics/sales/top-products",
    params,
    "Greška pri učitavanju top proizvoda"
  );
}

export async function getTopProductsAdvanced(
  top = 10,
  fromDate?: string,
  toDate?: string,
  useCached = true,
  storeId?: number | null,
  supplierId?: number | null
): Promise<TopProductsAdvancedResult> {
  const params = new URLSearchParams({ top: String(top) });
  appendFilterParams(params, fromDate, toDate, storeId, supplierId);

  return fetchJson(
    useCached ? "/api/analytics/cached/sales/top-products-advanced" : "/api/analytics/sales/top-products-advanced",
    params,
    "Greška pri učitavanju naprednih top proizvoda"
  );
}

export async function getInventoryStatus(
  lowStockThreshold = 2,
  useCached = true,
  fromDate?: string,
  toDate?: string,
  storeId?: number | null,
  supplierId?: number | null
): Promise<InventoryStatus> {
  const params = new URLSearchParams({ lowStockThreshold: String(lowStockThreshold) });
  appendFilterParams(params, fromDate, toDate, storeId, supplierId);

  return fetchJson(
    useCached ? "/api/analytics/cached/inventory/status" : "/api/analytics/inventory/status",
    params,
    "Greška pri učitavanju statusa zaliha"
  );
}

export async function getDailySales(
  fromDate?: string,
  toDate?: string,
  useCached = true,
  storeId?: number | null,
  supplierId?: number | null
): Promise<DailySale[]> {
  const payload = await getDailySalesPayload(fromDate, toDate, useCached, storeId, supplierId);
  return payload.items;
}

export async function getDailySalesPayload(
  fromDate?: string,
  toDate?: string,
  useCached = true,
  storeId?: number | null,
  supplierId?: number | null
): Promise<{ items: DailySale[]; meta: AnalyticsResponseMeta | null }> {
  const params = new URLSearchParams();
  appendFilterParams(params, fromDate, toDate, storeId, supplierId);

  const payload = await fetchJson<DailySale[] | { items?: DailySale[]; meta?: AnalyticsResponseMeta | null }>(
    useCached ? "/api/analytics/cached/sales/daily" : "/api/analytics/sales/daily",
    params,
    "Greška pri učitavanju dnevne prodaje"
  );

  if (Array.isArray(payload)) {
    return { items: payload, meta: null };
  }

  return {
    items: Array.isArray(payload.items) ? payload.items : [],
    meta: payload.meta ?? null,
  };
}

export async function getByCategory(
  fromDate?: string,
  toDate?: string,
  useCached = true,
  storeId?: number | null,
  supplierId?: number | null
): Promise<CategoryData[]> {
  const params = new URLSearchParams();
  appendFilterParams(params, fromDate, toDate, storeId, supplierId);

  return fetchJson(
    useCached ? "/api/analytics/cached/sales/by-category" : "/api/analytics/sales/by-category",
    params,
    "Greška pri učitavanju prodaje po kategorijama"
  );
}

export async function getByGender(
  fromDate?: string,
  toDate?: string,
  useCached = true,
  storeId?: number | null,
  supplierId?: number | null
): Promise<GenderData[]> {
  const params = new URLSearchParams();
  appendFilterParams(params, fromDate, toDate, storeId, supplierId);

  return fetchJson(
    useCached ? "/api/analytics/cached/sales/by-gender" : "/api/analytics/sales/by-gender",
    params,
    "Greška pri učitavanju prodaje po polu"
  );
}

export async function getBySupplier(
  fromDate?: string,
  toDate?: string,
  useCached = true,
  storeId?: number | null,
  supplierId?: number | null
): Promise<SupplierData[]> {
  const params = new URLSearchParams();
  appendFilterParams(params, fromDate, toDate, storeId, supplierId);

  return fetchJson(
    useCached ? "/api/analytics/cached/sales/by-supplier" : "/api/analytics/sales/by-supplier",
    params,
    "Greška pri učitavanju prodaje po dobavljačima"
  );
}

export async function getByWeekday(
  fromDate?: string,
  toDate?: string,
  useCached = true,
  storeId?: number | null,
  supplierId?: number | null
): Promise<WeekdayData[]> {
  const params = new URLSearchParams();
  appendFilterParams(params, fromDate, toDate, storeId, supplierId);

  return fetchJson(
    useCached ? "/api/analytics/cached/sales/by-weekday" : "/api/analytics/sales/by-weekday",
    params,
    "Greška pri učitavanju prodaje po danima"
  );
}

export async function getByHour(
  fromDate?: string,
  toDate?: string,
  useCached = true,
  storeId?: number | null,
  supplierId?: number | null
): Promise<HourData[]> {
  const params = new URLSearchParams();
  appendFilterParams(params, fromDate, toDate, storeId, supplierId);

  return fetchJson(
    useCached ? "/api/analytics/cached/sales/by-hour" : "/api/analytics/sales/by-hour",
    params,
    "Greška pri učitavanju prodaje po satima"
  );
}

export async function getByPayment(
  fromDate?: string,
  toDate?: string,
  useCached = true,
  storeId?: number | null,
  supplierId?: number | null
): Promise<PaymentData[]> {
  const params = new URLSearchParams();
  appendFilterParams(params, fromDate, toDate, storeId, supplierId);

  return fetchJson(
    useCached ? "/api/analytics/cached/sales/by-payment" : "/api/analytics/sales/by-payment",
    params,
    "Greška pri učitavanju prodaje po načinu plaćanja"
  );
}

export async function getCategoryTrends(
  fromDate?: string,
  toDate?: string,
  useCached = true,
  storeId?: number | null,
  supplierId?: number | null
): Promise<CategoryTrendPoint[]> {
  const params = new URLSearchParams();
  appendFilterParams(params, fromDate, toDate, storeId, supplierId);

  return fetchJson(
    useCached ? "/api/analytics/cached/sales/category-trends" : "/api/analytics/sales/category-trends",
    params,
    "Greška pri učitavanju trendova kategorija"
  );
}

export async function getTransactionStats(
  fromDate?: string,
  toDate?: string,
  useCached = true,
  storeId?: number | null,
  supplierId?: number | null
): Promise<TransactionStats> {
  const params = new URLSearchParams();
  appendFilterParams(params, fromDate, toDate, storeId, supplierId);

  return fetchJson(
    useCached ? "/api/analytics/cached/sales/transaction-stats" : "/api/analytics/sales/transaction-stats",
    params,
    "Greška pri učitavanju statistike transakcija"
  );
}

export async function getQuickInsights(
  fromDate?: string,
  toDate?: string,
  useCached = true,
  storeId?: number | null,
  supplierId?: number | null
): Promise<QuickInsights> {
  const params = new URLSearchParams();
  appendFilterParams(params, fromDate, toDate, storeId, supplierId);

  return fetchJson(
    useCached ? "/api/analytics/cached/quick-insights" : "/api/analytics/quick-insights",
    params,
    "Greška pri učitavanju brzih uvida"
  );
}

export async function getReorderSuggestions(
  useCached = true,
  supplierId?: number | null
): Promise<ReorderSuggestion[]> {
  const params = new URLSearchParams();
  if (supplierId != null) params.append("supplierId", String(supplierId));

  return fetchJson(
    useCached ? "/api/analytics/cached/reorder-suggestions" : "/api/analytics/reorder-suggestions",
    params,
    "Greška pri učitavanju predloga za dopunu zaliha"
  );
}

export async function getDashboardAdvanced(
  fromDate?: string,
  toDate?: string,
  useCached = true,
  storeId?: number | null,
  supplierId?: number | null
): Promise<DashboardAdvancedSnapshot> {
  const params = new URLSearchParams();
  appendFilterParams(params, fromDate, toDate, storeId, supplierId);

  return fetchJson(
    useCached ? "/api/analytics/cached/dashboard/advanced" : "/api/analytics/dashboard/advanced",
    params,
    "Greška pri učitavanju advanced dashboard metrika",
    undefined,
    dashboardAdvancedResponseSchema,
  );
}

export async function getDashboardBootstrap(
  fromDate?: string,
  toDate?: string,
  _useCached = true,
  storeId?: number | null,
  supplierId?: number | null
): Promise<AnalyticsDashboardBootstrap> {
  const params = new URLSearchParams();
  appendFilterParams(params, fromDate, toDate, storeId, supplierId);

  return fetchJson(
    "/api/analytics/cached/dashboard/bootstrap",
    params,
    "Greška pri učitavanju analytics dashboard bootstrapa",
    undefined,
    dashboardBootstrapResponseSchema,
  );
}

export async function getAnalyticsRefreshStatus(): Promise<AnalyticsRefreshStatus> {
  return fetchJson(
    "/api/analytics/refresh-status",
    undefined,
    "Greška pri učitavanju statusa osvežavanja analitike"
  );
}

export async function getAnalyticsCacheStatus(): Promise<AnalyticsCacheStatus> {
  return fetchJson(
    "/api/analytics/cached/cache/status",
    undefined,
    "Greška pri učitavanju statusa analytics cache-a"
  );
}

export function invalidateAnalyticsCache(options?: { broadcast?: boolean }): void {
  clientCacheGeneration += 1;
  responseCache.clear();
  inFlightRequests.clear();
  if (options?.broadcast !== false) {
    try {
      getAnalyticsCacheChannel()?.postMessage({ type: "invalidate", at: Date.now() });
    } catch {
      // Other tabs fall back to their short client TTL.
    }
  }
}

export async function clearAnalyticsCache(family = "all", adminKey?: string): Promise<AnalyticsCacheInvalidateResponse> {
  const params = new URLSearchParams();
  if (family.trim()) params.set("family", family.trim());
  const headers: Record<string, string> = {};
  if (adminKey?.trim()) {
    headers["X-Admin-Key"] = adminKey.trim();
  }
  const response = await fetchAnalyticsResponse(
    makeUrl("/api/analytics/cached/cache/invalidate", params),
    {
      method: "POST",
      headers,
    },
    DEFAULT_ANALYTICS_GET_TIMEOUT_MS
  );

  if (!response.ok) {
    throw new Error(await parseApiError(response, "Greška pri čišćenju analytics cache-a"));
  }

  // The server cache is gone; browser copies (this tab and others) must not keep serving it.
  invalidateAnalyticsCache();
  return (await response.json()) as AnalyticsCacheInvalidateResponse;
}

export async function getProductDecisionCenter(options?: {
  fromDate?: string;
  toDate?: string;
  storeId?: number | null;
  supplierId?: number | null;
  top?: number;
  dataScope?: string | null;
  search?: string | null;
  detailProductId?: number | null;
}): Promise<ProductDecisionCenterResponse> {
  const params = new URLSearchParams();
  if (options?.fromDate) params.append("fromDate", options.fromDate);
  if (options?.toDate) params.append("toDate", options.toDate);
  if (options?.storeId != null) params.append("storeId", String(options.storeId));
  if (options?.supplierId != null) params.append("supplierId", String(options.supplierId));
  if (options?.top != null) params.append("top", String(options.top));
  if (options?.dataScope) params.append("dataScope", options.dataScope);
  if (options?.search?.trim()) params.append("search", options.search.trim());
  if (options?.detailProductId != null) params.append("detailProductId", String(options.detailProductId));

  return fetchJson(
    "/api/analytics/cached/products/decision-center",
    params,
    "Greška pri učitavanju pregleda odluka o proizvodima",
    undefined,
    productDecisionCenterResponseSchema,
  );
}

export async function getProductDecisionTimeline(options?: {
  fromDate?: string;
  toDate?: string;
  sourceType?: string | null;
  sourceKey?: string | null;
  productId?: number | null;
  recommendationType?: string | null;
}): Promise<ProductDecisionTimelineFilterResponse> {
  const params = new URLSearchParams();
  if (options?.fromDate) params.append("fromDate", options.fromDate);
  if (options?.toDate) params.append("toDate", options.toDate);
  if (options?.sourceType) params.append("sourceType", options.sourceType);
  if (options?.sourceKey) params.append("sourceKey", options.sourceKey);
  if (options?.productId != null) params.append("productId", String(options.productId));
  if (options?.recommendationType) params.append("recommendationType", options.recommendationType);

  return fetchJson(
    "/api/analytics/cached/products/decision-center/timeline",
    params,
    "Greška pri učitavanju vremenske linije odluka"
  );
}

export async function getProductDecisionTimelineExportCsv(options?: {
  fromDate?: string;
  toDate?: string;
  sourceType?: string | null;
  sourceKey?: string | null;
  productId?: number | null;
  recommendationType?: string | null;
}): Promise<string> {
  const params = new URLSearchParams();
  if (options?.fromDate) params.append("fromDate", options.fromDate);
  if (options?.toDate) params.append("toDate", options.toDate);
  if (options?.sourceType) params.append("sourceType", options.sourceType);
  if (options?.sourceKey) params.append("sourceKey", options.sourceKey);
  if (options?.productId != null) params.append("productId", String(options.productId));
  if (options?.recommendationType) params.append("recommendationType", options.recommendationType);
  params.append("format", "csv");

  const url = makeUrl("/api/analytics/cached/products/decision-center/timeline/export", params);
  const response = await fetchAnalyticsResponse(url, undefined, DEFAULT_ANALYTICS_GET_TIMEOUT_MS);
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    const payload = (await response.json()) as ProductDecisionTimelineExportResponse;
    assertAnalyticsMetaSuccess(payload, "Izvoz vremenske linije odluka trenutno nije dostupan.");
    throw new Error("Izvoz vremenske linije odluka nije vratio CSV dokument.");
  }

  if (!response.ok) {
    throw new Error(await parseApiError(response, "Izvoz vremenske linije odluka trenutno nije dostupan."));
  }

  const csv = await response.text();
  if (/^# success=false/m.test(csv)) {
    throw new AnalyticsMetaError("Izvoz vremenske linije odluka trenutno nije dostupan.");
  }

  return csv;
}

function readFilterFallbackMeta(response: Response): AnalyticsResponseMeta | null {
  const fallbackFlag = response.headers.get("X-Analytics-Fallback") ?? response.headers.get("x-analytics-fallback");
  if (fallbackFlag?.toLowerCase() !== "true") {
    return null;
  }

  const warningCode = response.headers.get("X-Analytics-Fallback-Code")
    ?? response.headers.get("x-analytics-fallback-code")
    ?? "filter_fallback";
  const warningMessage = response.headers.get("X-Analytics-Fallback-Reason")
    ?? response.headers.get("x-analytics-fallback-reason")
    ?? "Filteri trenutno koriste pomoćni signal.";

  return {
    success: true,
    warningCode,
    warningMessage,
    dataQualityStatus: "warning",
    isPartial: true,
  };
}

function attachFilterFallbackMeta<T>(response: Response, payload: T): T {
  if (!Array.isArray(payload)) {
    return payload;
  }

  const meta = readFilterFallbackMeta(response);
  if (!meta) {
    return payload;
  }

  Object.defineProperty(payload, "meta", {
    value: meta,
    enumerable: false,
    configurable: true,
    writable: true,
  });

  return payload;
}

export async function getDecisionBoardAggregate(options?: {
  fromDate?: string;
  toDate?: string;
  storeId?: number | null;
  supplierId?: number | null;
  dataScope?: string | null;
  category?: string | null;
  gender?: string | null;
  seasonId?: number | null;
  minRevenue?: number | null;
  onlyHighConfidence?: boolean | null;
  excludeOosBeforeMarkdown?: boolean | null;
  search?: string | null;
}): Promise<DecisionBoardAggregateResponse> {
  const params = new URLSearchParams();
  if (options?.fromDate) params.append("fromDate", options.fromDate);
  if (options?.toDate) params.append("toDate", options.toDate);
  if (options?.storeId != null) params.append("storeId", String(options.storeId));
  if (options?.supplierId != null) params.append("supplierId", String(options.supplierId));
  if (options?.dataScope) params.append("dataScope", options.dataScope);
  if (options?.category) params.append("category", options.category);
  if (options?.gender) params.append("gender", options.gender);
  if (options?.seasonId != null) params.append("seasonId", String(options.seasonId));
  if (options?.minRevenue != null) params.append("minRevenue", String(options.minRevenue));
  if (options?.onlyHighConfidence != null) params.append("onlyHighConfidence", String(options.onlyHighConfidence));
  if (options?.excludeOosBeforeMarkdown != null) params.append("excludeOosBeforeMarkdown", String(options.excludeOosBeforeMarkdown));
  if (options?.search?.trim()) params.append("search", options.search.trim());

  return fetchJson(
    "/api/analytics/decision-board",
    params,
    "Greška pri učitavanju executive decision board pregleda",
    undefined,
    decisionBoardAggregateResponseSchema,
  );
}

export async function getStores(useCached = true, dataScope?: string | null): Promise<AnalyticsArrayWithMeta<StoreOption>> {
  const params = dataScope ? new URLSearchParams({ dataScope }) : undefined;
  return fetchJson<AnalyticsArrayWithMeta<StoreOption>>(
    useCached ? "/api/analytics/cached/filters/stores" : "/api/analytics/filters/stores",
    params,
    "Greška pri učitavanju prodavnica",
    attachFilterFallbackMeta
  );
}

export async function getSupplierFilters(
  fromDate?: string,
  toDate?: string,
  _useCached = true,
  storeId?: number | null,
  dataScope?: string | null
): Promise<AnalyticsArrayWithMeta<SupplierFilterOption>> {
  const params = new URLSearchParams();
  if (fromDate) params.append("fromDate", fromDate);
  if (toDate) params.append("toDate", toDate);
  if (storeId != null) params.append("storeId", String(storeId));
  if (dataScope != null && dataScope !== "") {
    params.set("dataScope", normalizeDataScope(dataScope));
  }

  return fetchJson<AnalyticsArrayWithMeta<SupplierFilterOption>>(
    "/api/analytics/cached/filters/suppliers",
    params,
    "Greška pri učitavanju filtera dobavljača",
    attachFilterFallbackMeta
  );
}

export async function getValidationCompleteness(
  useCached = true
): Promise<DashboardValidationEndpoint> {
  return fetchJson(
    useCached ? "/api/analytics/cached/validation/completeness" : "/api/analytics/validation/completeness",
    undefined,
    "Greška pri učitavanju completeness validacije"
  );
}

export async function getValidationFreshness(
  useCached = true
): Promise<DashboardValidationEndpoint> {
  return fetchJson(
    useCached ? "/api/analytics/cached/validation/freshness" : "/api/analytics/validation/freshness",
    undefined,
    "Greška pri učitavanju freshness validacije"
  );
}

export async function getValidationLostSales(
  useCached = true
): Promise<DashboardValidationEndpoint> {
  return fetchJson(
    useCached ? "/api/analytics/cached/validation/lost-sales" : "/api/analytics/validation/lost-sales",
    undefined,
    "Greška pri učitavanju lost-sales validacije"
  );
}

export async function getValidationNegativeQty(
  fromDate?: string,
  toDate?: string,
  useCached = true
): Promise<DashboardValidationEndpoint> {
  const params = new URLSearchParams();
  if (fromDate) params.append("fromDate", fromDate);
  if (toDate) params.append("toDate", toDate);

  return fetchJson(
    useCached ? "/api/analytics/cached/validation/negative-qty" : "/api/analytics/validation/negative-qty",
    params,
    "Greška pri učitavanju negative-qty validacije"
  );
}

export async function getDataQualityIssues(paramsInput: {
  type: DataQualityIssueType;
  page?: number;
  pageSize?: number;
  q?: string;
  sortBy?: DataQualitySortBy;
  sortDir?: DataQualitySortDir;
  dataScope?: string | null;
}): Promise<DataQualityIssueListResult> {
  const params = new URLSearchParams();
  params.set("type", paramsInput.type);
  params.set("page", String(paramsInput.page ?? 1));
  params.set("pageSize", String(paramsInput.pageSize ?? 25));
  if (paramsInput.q?.trim()) params.set("q", paramsInput.q.trim());
  if (paramsInput.sortBy) params.set("sortBy", paramsInput.sortBy);
  if (paramsInput.sortDir) params.set("sortDir", paramsInput.sortDir);
  if (paramsInput.dataScope) params.set("dataScope", paramsInput.dataScope);

  return fetchJson(
    "/api/analytics/data-quality/list",
    params,
    "Greška pri učitavanju data quality problema",
    undefined,
    dataQualityIssueListResponseSchema,
  );
}

export async function getAnalyticsDataQualityHealth(
  lookbackDays?: number,
  dataScope?: string | null,
  period?: { fromDate?: string | null; toDate?: string | null }
): Promise<AnalyticsDataQualityHealth> {
  const params = new URLSearchParams();
  if (lookbackDays != null) params.set("lookbackDays", String(lookbackDays));
  if (dataScope) params.set("dataScope", dataScope);
  if (period?.fromDate) params.set("fromDate", period.fromDate);
  if (period?.toDate) {
    const end = new Date(`${period.toDate}T00:00:00.000Z`);
    if (Number.isFinite(end.getTime())) {
      end.setUTCDate(end.getUTCDate() + 1);
      params.set("toDate", end.toISOString().slice(0, 10));
    }
  }

  return fetchJson(
    "/api/analytics/data-quality/health",
    params,
    "Greška pri učitavanju data quality health pregleda",
    undefined,
    dataQualityHealthResponseSchema,
  );
}

export async function getDataQualityTopOffenders(
  issueType: DataQualityIssueType,
  limit = 10,
  dataScope?: string | null
): Promise<DataQualityTopOffendersResult> {
  const params = new URLSearchParams();
  params.set("issueType", issueType);
  params.set("limit", String(limit));
  if (dataScope) params.set("dataScope", dataScope);

  return fetchJson(
    "/api/analytics/data-quality/top-offenders",
    params,
    "Greška pri učitavanju top data quality problema",
    undefined,
    dataQualityTopOffendersResponseSchema,
  );
}

export async function getAnalyticsDataQualityTrend(
  days = 7,
  dataScope?: string | null
): Promise<DataQualityTrendResult> {
  const params = new URLSearchParams();
  params.set("days", String(days));
  if (dataScope) params.set("dataScope", dataScope);

  return fetchJson(
    "/api/analytics/data-quality/trend",
    params,
    "Greška pri učitavanju data quality trenda",
    undefined,
    dataQualityTrendResponseSchema,
  );
}

export async function getPilotDataQualityIntakeReport(paramsInput: {
  fromDate?: string | null;
  toDate?: string | null;
  storeId?: number | null;
  supplierId?: number | null;
  dataScope?: string | null;
}): Promise<PilotDataQualityIntakeReport> {
  const params = new URLSearchParams();
  if (paramsInput.fromDate) params.set("fromDate", paramsInput.fromDate);
  if (paramsInput.toDate) params.set("toDate", paramsInput.toDate);
  if (paramsInput.storeId != null) params.set("storeId", String(paramsInput.storeId));
  if (paramsInput.supplierId != null) params.set("supplierId", String(paramsInput.supplierId));
  if (paramsInput.dataScope) params.set("dataScope", paramsInput.dataScope);

  return fetchJson(
    "/api/analytics/data-quality/intake-report",
    params,
    "Greška pri učitavanju pilot izveštaja",
    undefined,
    pilotDataQualityIntakeResponseSchema,
  );
}

export async function getSupplierDecisionDurableReport(paramsInput: {
  fromDate?: string | null;
  toDate?: string | null;
  storeId?: number | null;
  supplierId?: number | null;
  scope?: string | null;
  dataScope?: string | null;
  category?: string | null;
  gender?: string | null;
  seasonId?: number | null;
  minRevenue?: number | null;
  onlyHighConfidence?: boolean | null;
  excludeOosBeforeMarkdown?: boolean | null;
  section?: string | null;
}): Promise<SupplierDecisionDurableReport> {
  const params = new URLSearchParams();
  appendSupplierDecisionReportQuery(params, paramsInput);

  return fetchJson(
    "/api/analytics/reports/supplier-decision",
    params,
    "Greška pri učitavanju izveštaja dobavljača",
    undefined,
    durableAnalyticsReportResponseSchema,
  );
}

export async function getPilotIntakeDurableReport(paramsInput: {
  fromDate?: string | null;
  toDate?: string | null;
  storeId?: number | null;
  supplierId?: number | null;
  scope?: string | null;
  dataScope?: string | null;
  refresh?: boolean | null;
}): Promise<PilotIntakeDurableReport> {
  const params = new URLSearchParams();
  if (paramsInput.fromDate) params.set("fromDate", paramsInput.fromDate);
  if (paramsInput.toDate) params.set("toDate", paramsInput.toDate);
  if (paramsInput.storeId != null) params.set("storeId", String(paramsInput.storeId));
  if (paramsInput.supplierId != null) params.set("supplierId", String(paramsInput.supplierId));
  if (paramsInput.scope) params.set("scope", paramsInput.scope);
  if (paramsInput.dataScope) params.set("dataScope", paramsInput.dataScope);
  if (paramsInput.refresh) params.set("refresh", "true");

  return fetchJson(
    "/api/analytics/reports/pilot-intake",
    params,
    "Greška pri učitavanju trajnog pilot izveštaja",
    undefined,
    durableAnalyticsReportResponseSchema,
  );
}

export async function getInventoryBalance(
  useCached = true,
  storeId?: number | null,
  supplierId?: number | null,
  dataScope?: string | null,
  signal?: AbortSignal,
): Promise<import("../types/analytics").InventoryBalance> {
  const params = new URLSearchParams();
  if (storeId != null) params.append("storeId", String(storeId));
  if (supplierId != null) params.append("supplierId", String(supplierId));
  if (dataScope != null && dataScope !== "") params.append("dataScope", normalizeDataScope(dataScope));

  return fetchJson(
    useCached ? "/api/analytics/cached/inventory/balance" : "/api/analytics/inventory/balance",
    params,
    "Greška pri učitavanju bilansa zaliha",
    undefined,
    inventoryBalanceResponseSchema,
    signal,
  );
}

export async function getInventoryList(
  options?: {
    pageNumber?: number;
    pageSize?: number;
    search?: string;
    storeId?: number | null;
    supplierId?: number | null;
    sortBy?: string | null;
    fromDate?: string | null;
    toDate?: string | null;
    dataScope?: string | null;
    signal?: AbortSignal;
  }
): Promise<import("../types/analytics").InventoryPagedResponse> {
  const pageNumber = options?.pageNumber ?? 1;
  const pageSize = options?.pageSize ?? 50;
  const params = new URLSearchParams({ page: String(pageNumber), pageSize: String(pageSize) });
  if (options?.search) params.append("search", options.search);
  if (options?.storeId != null) params.append("storeId", String(options.storeId));
  if (options?.supplierId != null) params.append("supplierId", String(options.supplierId));
  if (options?.sortBy) params.append("sortBy", options.sortBy);
  if (options?.fromDate) params.append("fromDate", options.fromDate);
  if (options?.toDate) params.append("toDate", options.toDate);
  if (options?.dataScope != null && options.dataScope !== "") params.append("dataScope", normalizeDataScope(options.dataScope));

  if (options?.signal) {
    const url = makeUrl("/api/analytics/cached/inventory/list", params);
    const res = await fetchWithTimeout(url, { signal: options.signal }, DEFAULT_ANALYTICS_GET_TIMEOUT_MS);
    if (!res.ok) {
      throw new Error(await parseApiError(res, "Greška pri učitavanju liste zaliha"));
    }
    const payload = (await res.json()) as import("../types/analytics").InventoryPagedResponse;
    const checkedPayload = assertAnalyticsMetaSuccess(payload, "Greška pri učitavanju liste zaliha");
    return validateAnalyticsResponse(
      checkedPayload,
      inventoryPagedResponseSchema,
      "Lista zaliha",
    );
  }

  return fetchJson(
    "/api/analytics/cached/inventory/list",
    params,
    "Greška pri učitavanju liste zaliha",
    undefined,
    inventoryPagedResponseSchema,
    options?.signal,
  );
}

export async function getInventoryInsights(options?: {
  search?: string;
  storeId?: number | null;
  supplierId?: number | null;
  sortBy?: string | null;
  dataScope?: string | null;
  signal?: AbortSignal;
}): Promise<InventoryInsights> {
  const params = new URLSearchParams();
  if (options?.search) params.append("search", options.search);
  if (options?.storeId != null) params.append("storeId", String(options.storeId));
  if (options?.supplierId != null) params.append("supplierId", String(options.supplierId));
  if (options?.sortBy) params.append("sortBy", options.sortBy);
  if (options?.dataScope != null && options.dataScope !== "") params.append("dataScope", normalizeDataScope(options.dataScope));

  return fetchJsonWithCachedFallback(
    "/api/analytics/cached/inventory/insights",
    "/api/analytics/inventory/insights",
    params,
    "Greška pri učitavanju inventory uvida",
    inventoryInsightsResponseSchema,
    options?.signal,
  );
}

export async function getInventoryItemDetail(
  id: number,
  options?: {
    storeId?: number | null;
    supplierId?: number | null;
    fromDate?: string | null;
    toDate?: string | null;
    dataScope?: string | null;
    signal?: AbortSignal;
  }
): Promise<InventoryItemDetail> {
  const params = new URLSearchParams();
  appendFilterParams(params, options?.fromDate ?? undefined, options?.toDate ?? undefined, options?.storeId, options?.supplierId);
  if (options?.dataScope != null && options.dataScope !== "") params.append("dataScope", normalizeDataScope(options.dataScope));

  return fetchJson(
    `/api/analytics/inventory/${id}/detail`,
    params,
    "Greška pri učitavanju detalja artikla",
    undefined,
    inventoryDetailResponseSchema,
    options?.signal,
  );
}

export async function exportInventoryReport(options: {
  format: "pdf" | "xlsx" | "csv";
  orientation?: "portrait" | "landscape";
  includeFiltersAndMetadata?: boolean;
  forceAsync?: boolean;
  search?: string;
  storeId?: number | null;
  supplierId?: number | null;
  sortBy?: string | null;
  dataScope?: string | null;
}): Promise<DocumentOperationResponse> {
  const adminKey = ensureExportAdminKey("izvoz zaliha");
  if (!adminKey) {
    throw new Error("Admin key je obavezan za izvoz dokumenata.");
  }

  return postJson(
    "/api/analytics/inventory/export",
    {
      format: options.format,
      orientation: options.orientation ?? "landscape",
      includeFiltersAndMetadata: options.includeFiltersAndMetadata ?? true,
      forceAsync: options.forceAsync ?? false,
      search: options.search,
      storeId: options.storeId,
      supplierId: options.supplierId,
      sortBy: options.sortBy,
      dataScope: options.dataScope,
    },
    "Greška pri server-side eksportu bilansa",
    adminKey
  );
}

export async function previewInventoryReport(options?: {
  orientation?: "portrait" | "landscape";
  includeFiltersAndMetadata?: boolean;
  search?: string;
  storeId?: number | null;
  supplierId?: number | null;
  sortBy?: string | null;
  dataScope?: string | null;
}): Promise<DocumentOperationResponse> {
  const adminKey = ensureExportAdminKey("pregled stampe zaliha");
  if (!adminKey) {
    throw new Error("Admin key je obavezan za izvoz dokumenata.");
  }

  return postJson(
    "/api/analytics/inventory/print-preview",
    {
      orientation: options?.orientation ?? "landscape",
      includeFiltersAndMetadata: options?.includeFiltersAndMetadata ?? true,
      search: options?.search,
      storeId: options?.storeId,
      supplierId: options?.supplierId,
      sortBy: options?.sortBy,
      dataScope: options?.dataScope,
    },
    "Greška pri pripremi print preview-a",
    adminKey
  );
}

export async function printBlankInventoryForm(options?: {
  orientation?: "portrait" | "landscape";
}): Promise<DocumentOperationResponse> {
  const params = new URLSearchParams();
  if (options?.orientation) params.set("orientation", options.orientation);
  const qs = params.size > 0 ? `?${params.toString()}` : "";
  const adminKey = ensureExportAdminKey("prazan obrazac zaliha");
  if (!adminKey) {
    throw new Error("Admin key je obavezan za izvoz dokumenata.");
  }

  return postJson(
    `/api/analytics/inventory/print-blank${qs}`,
    {},
    "Greška pri pripremi praznog obrasca za štampu",
    adminKey
  );
}

export async function getInventoryStoreComparison(options?: {
  compareStoreIds?: number[];
  supplierId?: number | null;
  search?: string;
  dataScope?: string | null;
  signal?: AbortSignal;
}): Promise<InventoryStoreComparison> {
  const params = new URLSearchParams();
  for (const storeId of options?.compareStoreIds ?? []) {
    params.append("compareStoreIds", String(storeId));
  }
  if (options?.supplierId != null) params.append("supplierId", String(options.supplierId));
  if (options?.search) params.append("search", options.search);
  if (options?.dataScope != null && options.dataScope !== "") params.append("dataScope", normalizeDataScope(options.dataScope));

  return fetchJsonWithCachedFallback(
    "/api/analytics/cached/inventory/store-comparison",
    "/api/analytics/inventory/store-comparison",
    params,
    "Greška pri učitavanju poređenja prodavnica",
    inventoryStoreComparisonResponseSchema,
    options?.signal,
  );
}

export async function getInventoryActionSuggestions(options?: {
  storeId?: number | null;
  supplierId?: number | null;
  search?: string;
  dataScope?: string | null;
  signal?: AbortSignal;
}): Promise<InventoryActionWorkflow> {
  const params = new URLSearchParams();
  if (options?.storeId != null) params.append("storeId", String(options.storeId));
  if (options?.supplierId != null) params.append("supplierId", String(options.supplierId));
  if (options?.search) params.append("search", options.search);
  if (options?.dataScope != null && options.dataScope !== "") params.append("dataScope", normalizeDataScope(options.dataScope));

  return fetchJson(
    "/api/analytics/inventory/action-suggestions",
    params,
    "Greška pri učitavanju predloga akcije",
    undefined,
    inventoryActionWorkflowResponseSchema,
    options?.signal,
  );
}

export async function saveInventoryActionDecision(
  suggestionKey: string,
  input: InventoryActionDecisionInput
): Promise<{
  suggestionKey: string;
  actionType: string;
  status: string;
  note?: string | null;
  updatedAtUtc: string;
  updatedByUserName: string;
}> {
  return postJson(
    `/api/analytics/inventory/action-suggestions/${encodeURIComponent(suggestionKey)}/decision`,
    input,
    "Greška pri čuvanju odluke za predlog akcije"
  );
}

export async function getInventoryReportSchedules(): Promise<InventoryReportSchedule[]> {
  return fetchJson(
    "/api/analytics/inventory/report-schedules",
    undefined,
    "Greška pri učitavanju rasporeda za mail izveštaje"
  );
}

// ── Analytics Action Queue ─────────────────────────────────────────────────

export async function getAnalyticsActions(
  filters?: AnalyticsActionFilters
): Promise<AnalyticsActionListResponse> {
  const params = new URLSearchParams();
  if (filters?.status) params.append("status", filters.status);
  if (filters?.priority) params.append("priority", filters.priority);
  if (filters?.sourceType) params.append("sourceType", filters.sourceType);
  if (filters?.dataQualityStatus) params.append("dataQualityStatus", filters.dataQualityStatus);
  if (filters?.search) params.append("search", filters.search);
  if (filters?.createdFrom) params.append("createdFrom", filters.createdFrom);
  if (filters?.createdTo) params.append("createdTo", filters.createdTo);
  if (filters?.page != null) params.append("page", String(filters.page));
  if (filters?.pageSize != null) params.append("pageSize", String(filters.pageSize));
  return fetchJson<AnalyticsActionListResponse>(
    "/api/analytics/actions",
    params,
    "Greška pri učitavanju liste akcija",
    undefined,
    analyticsActionListResponseSchema,
  );
}

export async function getAnalyticsActionCounts(
  filters?: AnalyticsActionPopulationFilters
): Promise<AnalyticsActionCounts> {
  const params = new URLSearchParams();
  if (filters?.createdFrom) params.append("createdFrom", filters.createdFrom);
  if (filters?.createdTo) params.append("createdTo", filters.createdTo);
  if (filters?.sourceType) params.append("sourceType", filters.sourceType);
  if (filters?.priority) params.append("priority", filters.priority);
  if (filters?.dataQualityStatus) params.append("dataQualityStatus", filters.dataQualityStatus);
  return fetchJson<AnalyticsActionCounts>(
    "/api/analytics/actions/counts",
    params,
    "Greška pri učitavanju brojača akcija",
    undefined,
    analyticsActionCountsResponseSchema,
  );
}

export async function getAnalyticsActionOutcomeSummary(
  filters?: AnalyticsActionOutcomeSummaryFilters
): Promise<AnalyticsActionOutcomeSummaryResponse> {
  const params = new URLSearchParams();
  if (filters?.createdFrom) params.append("createdFrom", filters.createdFrom);
  if (filters?.createdTo) params.append("createdTo", filters.createdTo);
  if (filters?.resolvedFrom) params.append("resolvedFrom", filters.resolvedFrom);
  if (filters?.resolvedTo) params.append("resolvedTo", filters.resolvedTo);
  if (filters?.measuredFrom) params.append("measuredFrom", filters.measuredFrom);
  if (filters?.measuredTo) params.append("measuredTo", filters.measuredTo);
  if (filters?.sourceType) params.append("sourceType", filters.sourceType);
  if (filters?.priority) params.append("priority", filters.priority);
  if (filters?.dataQualityStatus) params.append("dataQualityStatus", filters.dataQualityStatus);

  return fetchJson<AnalyticsActionOutcomeSummaryResponse>(
    "/api/analytics/actions/outcomes/summary",
    params,
    "Greška pri učitavanju sažetka ishoda akcija",
    undefined,
    analyticsActionOutcomeSummaryResponseSchema,
  );
}

export async function getAnalyticsActionById(id: number): Promise<AnalyticsActionItem> {
  return fetchJson<AnalyticsActionItem>(
    `/api/analytics/actions/${id}`,
    undefined,
    "Greška pri učitavanju akcije"
  );
}

type AnalyticsActionUpsertResponse = {
  item: AnalyticsActionItem;
  created: boolean;
  existing: boolean;
  status: string;
  sourceKey: string;
};

type AnalyticsActionSourceStatusInput = {
  items: Array<{
    sourceType: string;
    sourceKey: string;
  }>;
} | {
  sourceType: string;
  sourceKeys: string[];
};

type AnalyticsActionSourceStatusResponse = {
  items: Array<{
    sourceType: string;
    sourceKey: string;
    exists: boolean;
    actionId?: number | null;
    status?: string | null;
    outcomeStatus?: string | null;
    canCreateNew?: boolean;
    hasEvidenceSnapshot?: boolean;
    evidenceSnapshotCapturedAtUtc?: string | null;
    evidenceSnapshotRecommendationId?: string | null;
  }>;
};

export async function upsertAnalyticsAction(
  input: AnalyticsActionUpsertInput
): Promise<AnalyticsActionItem> {
  const response = await upsertAnalyticsActionWithResult(input);
  return response.item;
}

export async function upsertAnalyticsActionWithResult(
  input: AnalyticsActionUpsertInput
): Promise<AnalyticsActionUpsertResponse> {
  return postJson<AnalyticsActionUpsertResponse>(
    "/api/analytics/actions",
    input,
    "Greška pri dodavanju akcije"
  );
}

export async function getAnalyticsActionSourceStatuses(
  input: AnalyticsActionSourceStatusInput
): Promise<AnalyticsActionSourceStatusResponse> {
  const payload = "items" in input
    ? input
    : {
      items: input.sourceKeys.map((sourceKey) => ({
        sourceType: input.sourceType,
        sourceKey,
      })),
    };

  return postJson<AnalyticsActionSourceStatusResponse>(
    "/api/analytics/actions/status",
    payload,
    "Greška pri proveri statusa akcija"
  );
}

export async function updateAnalyticsActionStatus(
  id: number,
  input: AnalyticsActionStatusUpdateInput
): Promise<AnalyticsActionItem> {
  return patchJson<AnalyticsActionItem>(
    `/api/analytics/actions/${id}/status`,
    input,
    "Greška pri ažuriranju statusa akcije"
  );
}

export async function updateAnalyticsActionOutcome(
  id: number,
  input: AnalyticsActionOutcomeUpdateInput
): Promise<AnalyticsActionItem> {
  return patchJson<AnalyticsActionItem>(
    `/api/analytics/actions/${id}/outcome`,
    input,
    "Greška pri ažuriranju ishoda akcije"
  );
}

export async function createInventoryReportSchedule(input: InventoryReportScheduleInput): Promise<InventoryReportSchedule> {
  return postJson(
    "/api/analytics/inventory/report-schedules",
    input,
    "Greška pri kreiranju rasporeda izveštaja"
  );
}

export async function updateInventoryReportSchedule(id: number, input: InventoryReportScheduleInput): Promise<InventoryReportSchedule> {
  const response = await fetchAnalyticsResponse(makeUrl(`/api/analytics/inventory/report-schedules/${id}`), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  }, 60_000);

  if (!response.ok) {
    throw new Error(await parseApiError(response, "Greška pri ažuriranju rasporeda izveštaja"));
  }

  return (await response.json()) as InventoryReportSchedule;
}

export async function runInventoryReportScheduleNow(id: number): Promise<InventoryScheduleRunResponse> {
  const adminKey = ensureExportAdminKey("pokretanje rasporeda izvestaja");
  if (!adminKey) {
    throw new Error("Admin key je obavezan za izvoz dokumenata.");
  }

  return postJson(
    `/api/analytics/inventory/report-schedules/${id}/run-now`,
    {},
    "Greška pri ručnom pokretanju rasporeda",
    adminKey
  );
}

// ── Demand Forecasting ──────────────────────────────────────────────────────────

export async function getForecast(options?: {
  storeId?: number | null;
  supplierId?: number | null;
  skuId?: number | null;
  sizeCode?: string;
  top?: number;
  fromDate?: string | null;
  toDate?: string | null;
  dataScope?: string | null;
  signal?: AbortSignal;
}): Promise<ForecastDto> {
  const params = new URLSearchParams();
  if (options?.storeId != null) params.append("storeId", String(options.storeId));
  if (options?.supplierId != null) params.append("supplierId", String(options.supplierId));
  if (options?.skuId != null) params.append("skuId", String(options.skuId));
  if (options?.sizeCode) params.append("sizeCode", options.sizeCode);
  if (options?.top != null) params.append("top", String(options.top));
  if (options?.fromDate) params.append("fromDate", options.fromDate);
  if (options?.toDate) params.append("toDate", options.toDate);
  if (options?.dataScope) params.append("dataScope", normalizeDataScope(options.dataScope));
  return fetchJson(
    "/api/analytics/cached/inventory/forecast",
    params,
    "Greška pri učitavanju forecast podataka",
    undefined,
    inventorySignalResponseSchema,
    options?.signal,
  );
}

export async function getForecastBaselineBacktest(options?: {
  storeId?: number | null;
  supplierId?: number | null;
  horizonDays?: number;
  signal?: AbortSignal;
}): Promise<ForecastBaselineBacktestDto> {
  const params = new URLSearchParams();
  if (options?.storeId != null) params.append("storeId", String(options.storeId));
  if (options?.supplierId != null) params.append("supplierId", String(options.supplierId));
  if (options?.horizonDays != null) params.append("horizonDays", String(options.horizonDays));

  return fetchJson(
    "/api/analytics/cached/inventory/forecast/backtest",
    params,
    "Evaluacija trend modela trenutno nije dostupna",
    undefined,
    undefined,
    options?.signal,
  );
}

// ── Size Curve Intelligence ─────────────────────────────────────────────────

export async function getSizeCurve(options?: {
  storeId?: number | null;
  supplierId?: number | null;
  skuId?: number | null;
  sizeCode?: string | null;
  top?: number;
  fromDate?: string | null;
  toDate?: string | null;
  dataScope?: string | null;
  signal?: AbortSignal;
}): Promise<SizeCurveDto> {
  const params = new URLSearchParams();
  if (options?.storeId != null) params.append("storeId", String(options.storeId));
  if (options?.supplierId != null) params.append("supplierId", String(options.supplierId));
  if (options?.skuId != null) params.append("skuId", String(options.skuId));
  if (options?.sizeCode != null && options.sizeCode.trim() !== "") params.append("sizeCode", options.sizeCode.trim());
  if (options?.top != null) params.append("top", String(options.top));
  if (options?.fromDate) params.append("fromDate", options.fromDate);
  if (options?.toDate) params.append("toDate", options.toDate);
  if (options?.dataScope) params.append("dataScope", normalizeDataScope(options.dataScope));
  return fetchJson(
    "/api/analytics/cached/inventory/size-curve",
    params,
    "Greška pri učitavanju size curve",
    undefined,
    inventorySignalResponseSchema,
    options?.signal,
  );
}

// ── Smart Rebalancing ─────────────────────────────────────────────────────────────

export async function getRebalanceSuggestions(options?: {
  fromStoreId?: number | null;
  toStoreId?: number | null;
  supplierId?: number | null;
  urgency?: string;
  top?: number;
  fromDate?: string | null;
  toDate?: string | null;
  dataScope?: string | null;
  signal?: AbortSignal;
}): Promise<RebalanceListDto> {
  const params = new URLSearchParams();
  if (options?.fromStoreId != null) params.append("fromStoreId", String(options.fromStoreId));
  if (options?.toStoreId != null) params.append("toStoreId", String(options.toStoreId));
  if (options?.supplierId != null) params.append("supplierId", String(options.supplierId));
  if (options?.urgency) params.append("urgency", options.urgency);
  if (options?.top != null) params.append("top", String(options.top));
  if (options?.fromDate) params.append("fromDate", options.fromDate);
  if (options?.toDate) params.append("toDate", options.toDate);
  if (options?.dataScope) params.append("dataScope", normalizeDataScope(options.dataScope));
  return fetchJson(
    "/api/analytics/cached/inventory/rebalance-suggestions",
    params,
    "Greška pri učitavanju predloga za redistribuciju",
    undefined,
    inventorySignalResponseSchema,
    options?.signal,
  );
}

// ── Inventory Alerts ────────────────────────────────────────────────────────────

export async function getInventoryAlerts(options?: {
  storeId?: number | null;
  supplierId?: number | null;
  severity?: string;
  top?: number;
  fromDate?: string | null;
  toDate?: string | null;
  dataScope?: string | null;
  signal?: AbortSignal;
}): Promise<InventoryAlertListDto> {
  const params = new URLSearchParams();
  if (options?.storeId != null) params.append("storeId", String(options.storeId));
  if (options?.supplierId != null) params.append("supplierId", String(options.supplierId));
  if (options?.severity) params.append("severity", options.severity);
  if (options?.top != null) params.append("top", String(options.top));
  if (options?.fromDate) params.append("fromDate", options.fromDate);
  if (options?.toDate) params.append("toDate", options.toDate);
  if (options?.dataScope) params.append("dataScope", normalizeDataScope(options.dataScope));
  return fetchJson(
    "/api/analytics/cached/inventory/alerts",
    params,
    "Greška pri učitavanju inventory alertova",
    undefined,
    inventorySignalResponseSchema,
    options?.signal,
  );
}

